import { useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog, F0Select, F0SelectItemProps } from '@factorialco/f0-react'
import { Briefcase } from '@factorialco/f0-react/icons/app'
import {
  AtsVacanciesAssignJobPostingSelectorDocument,
  AtsVacanciesAssignJobPostingDocument,
  AtsVacanciesTableDocument,
} from 'generated/resources/graphql'

import { statusLabel, statusDotColor } from 'modules/ats/components/JobStatusTag'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { useNavigate } from 'react-router-dom'
import { useNavigation } from 'modules/core/hooks/useNavigation'

interface Props {
  vacancyIds: number[]
  onClose: () => void
  onSuccess: () => void
}

export const AssignJobPostingModal = ({ vacancyIds, onClose, onSuccess }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [selectedJobPostingId, setSelectedJobPostingId] = useState<string | undefined>(undefined)
  const [fieldError, setFieldError] = useState<string | undefined>(undefined)

  const { loading: jobsLoading, data } = useQuery(AtsVacanciesAssignJobPostingSelectorDocument)

  const canCreateJobPostings =
    (data?.ats._policies.createJobPostings.can || data?.ats._policies.manageJobPostings.can) ??
    false

  const jobPostingOptions: F0SelectItemProps<string>[] = useMemo(
    () =>
      (data?.ats.jobPostingsConnection.nodes ?? []).map((jp) => {
        const descriptionParts = [
          [jp.location?.name, jp.workplaceTypeLabel ? `(${jp.workplaceTypeLabel})` : null]
            .filter(Boolean)
            .join(' '),
          [jp.jobCatalogTreeRole?.name, jp.jobCatalogTreeLevel?.name].filter(Boolean).join(' ') ||
            undefined,
          jp.team?.name,
        ].filter(Boolean)

        return {
          label: jp.title ?? '',
          value: String(jp.id),
          icon: Briefcase,
          tag: { type: 'dot', text: statusLabel(jp.status), color: statusDotColor(jp.status) },
          description: descriptionParts.length > 0 ? descriptionParts.join(' / ') : undefined,
        }
      }),
    [data?.ats.jobPostingsConnection.nodes]
  )

  const [assignJobPosting, { loading: assigning, client }] = useMutation(
    AtsVacanciesAssignJobPostingDocument,
    {
      extractorFn: (result) => result.atsMutations.bulkAssignJobPostingAtsVacancy,
    }
  )

  const handleAssign = async () => {
    if (!selectedJobPostingId) {
      setFieldError(i18n.t('ats.vacancies.assign_job_modal.validation_select_required'))
      return
    }

    setFieldError(undefined)
    const result = await assignJobPosting({
      variables: {
        ids: vacancyIds,
        jobPostingId: Number(selectedJobPostingId),
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ats.vacancies.assign_job_modal.error_message'),
      })
    })

    if (handled.success) {
      void client.refetchQueries({ include: [AtsVacanciesTableDocument] })
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.vacancies.assign_job_modal.success_message'),
      })

      onSuccess()
    }
  }

  return (
    <F0Dialog
      title={i18n.t('ats.vacancies.assign_job_modal.title')}
      isOpen
      onClose={onClose}
      primaryAction={{
        label: i18n.t('ats.vacancies.assign_job_modal.assign_button'),
        onClick: handleAssign,
        loading: assigning,
      }}
      secondaryAction={{
        label: i18n.t('ats.vacancies.assign_job_modal.cancel'),
        onClick: onClose,
      }}
    >
      <Box width="full" gap="s16">
        <F0Select
          label={i18n.t('ats.vacancies.assign_job_modal.select_label')}
          name="jobPosting"
          placeholder={i18n.t('ats.vacancies.assign_job_modal.select_placeholder')}
          size="md"
          value={selectedJobPostingId}
          onChange={(value: string) => {
            setSelectedJobPostingId(value)
            setFieldError(undefined)
          }}
          options={jobPostingOptions}
          showSearchBox
          searchBoxPlaceholder={i18n.t('ats.vacancies.assign_job_modal.search_placeholder')}
          searchEmptyMessage={i18n.t('ats.vacancies.assign_job_modal.search_no_results')}
          readonly={jobsLoading}
          error={fieldError}
          required
        />

        {canCreateJobPostings && (
          <F0Alert
            variant="neutral"
            title={i18n.t('ats.vacancies.assign_job_modal.create_job_section.title')}
            description={i18n.t('ats.vacancies.assign_job_modal.create_job_section.description')}
            icon={Briefcase}
            action={{
              label: i18n.t('ats.vacancies.assign_job_modal.create_job_section.new_job_button'),
              onClick: () => navigate(navigation.ats.hiringPlan.vacancies.newJob.fullPath()),
            }}
          />
        )}
      </Box>
    </F0Dialog>
  )
}
